import React from "react";
import { Input, Tooltip } from "antd";
import {
  escapeRegExp,
  copyTextToClipBoard,
  isEmpty,
} from "../../utils/common-util";
import Detector from "../../utils/detector";
import { DEFAULT_SORT_CONFIG } from '../../constants/app-constants';
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";

// generate form filter panel
function generateFilterPanel(props) {
  const { placeholder = "Search" } = props;
  return function ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
    return (
      <div className="custom-filter-dropdown">
        <Input
          ref={(ele) => (this.searchInput = ele)}
          placeholder={placeholder}
          value={selectedKeys ? selectedKeys[0] : ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={this.handleSearch(selectedKeys, confirm)}
          suffix={
            <CloseOutlined
              className="cursor-pointer"
              style={{ color: "#838383" }}
              theme="filled"
              onClick={this.handleReset(clearFilters)}
            />
          }
        />
      </div>
    );
  };
}

function generateFilterPanelHook(props, dataIndex) {
  const { placeholder = "Search", handleSearch, handleReset, inputRef } = props;
  return function ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
    return (
      <div className="custom-filter-dropdown">
        <Input
          ref={elem => {
            if (typeof inputRef.current === 'object') {
              inputRef.current[dataIndex] = elem
            } else {
              inputRef.current = elem
            }
          }}
          placeholder={placeholder}
          value={selectedKeys ? selectedKeys[0] : ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch(selectedKeys, confirm, dataIndex);
            }
          }}
          suffix={
            <CloseOutlined
              className="cursor-pointer"
              style={{ color: "#838383" }}
              theme="filled"
              onClick={handleReset(clearFilters, dataIndex)}
            />
          }
        />
      </div>
    );
  };
}

function newGenerateFilterPanelHook(props, dataIndex) {
  const {
    placeholder = "Search",
    handleSearch,
    handleReset,
    inputRef,
    name,
  } = props;
  return function ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
    return (
      <div className="custom-filter-dropdown">
        <Input
          ref={elem => {
            if (typeof inputRef.current === 'object') {
              inputRef.current[dataIndex] = elem
            } else {
              inputRef.current = elem
            }
          }}
          placeholder={placeholder}
          value={selectedKeys ? selectedKeys[0] : ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch(e.target.value, name, confirm, dataIndex);
            }
          }}
          suffix={
            <CloseOutlined
              className="cursor-pointer"
              style={{ color: "#838383" }}
              theme="filled"
              onClick={handleReset(clearFilters, { name }, dataIndex)}
            />
          }
        />
      </div>
    );
  };
}

function displayTextFilter2(filteredInfo, filterField, props = {}) {
  const searchText = (filteredInfo[filterField] || [])[0];
  return displayTextFilter(searchText, (props = {}));
}

// highlight text that's filtered
function displayTextFilter(searchText, props = {}) {
  return function (text) {
    if (searchText && !isEmpty(text)) {
      const searchTexts = searchText
        .toLowerCase()
        .split(" ")
        .map((t) => t.replace(/_+/g, " ")); // replace '_' in meaning space bar
      const searchEscapedTexts = searchTexts.map((t) => escapeRegExp(t));
      const regTexts = searchEscapedTexts.map((t) => `(${t})`).join("|");
      return (
        <span {...props}>
          {String(text)
            .split(new RegExp(regTexts, "i"))
            .filter((v) => !!v)
            .map((fragment, i) =>
              searchTexts.includes(fragment.toLowerCase()) ? (
                <span key={i} className="filtered-highlight">
                  {fragment}
                </span>
              ) : (
                fragment
              )
            )}
        </span>
      );
    } else {
      return <span {...props}>{text}</span>;
    }
  };
}

// focus intput element when filter panel visible
function onFilterDropdownVisibleChange(visible) {
  setTimeout(() => {
    if (visible && this.searchInput) {
      this.searchInput.focus();
    }
  }, 100);
}

function onFilterDropdownVisibleChangeHook(props, dataIndex) {
  return (visible) => {
    setTimeout(() => {
      if (visible) {
        if (typeof props.inputRef.current === 'object') {
          props.inputRef.current[dataIndex].focus()
        } else {
          props.inputRef.current.focus()
        }
      }
    }, 100); // 100 for prevent windows scroll up [dont know why]
  };
}

// icon
function filterIcon(filtered) {
  return (
    <SearchOutlined
      style={{ color: filtered ? "#108ee9" : "rgb(123, 121, 121)" }}
    />
  );
}

function replaceSpace(value) {
  if (value === undefined || value === null) {
    return value;
  }
  return String(value).replace(/\s+/g, "_");
}

// ** if return true then it will render this row
// ** field can be string or function or string array
function onFilterText(field) {
  return function (searchText, record) {
    let value;
    switch (typeof field) {
      case "string":
        value = replaceSpace(record[field]);
        break;
      case "function":
        value = replaceSpace(field(record));
        break;
      case "object":
        value = field
          .map((f) => replaceSpace(record[f]) || "")
          .join(" ")
          .trim();
        break;
      default:
        value = record[field];
        break;
    }
    if (isEmpty(value) || isEmpty(searchText)) {
      return false;
    }

    value = typeof field !== "string" ? String(value) : value;
    return searchText.split(" ").some((text) => {
      return value
        .toLowerCase()
        .includes(text.replace(/_+/g, "_").toLowerCase()); // replace '_' in meaning space bar
    });
  };
}

// get individual filter field
function filteredValue(filteredInfo, field) {
  if (!filteredInfo) return null;
  return filteredInfo[field] || null;
}

// manual filter
function filteredData(datas, filteredInfo) {
  const matchers = Object.entries(filteredInfo).map(([field, filterValue]) => ({
    field,
    matcher: new RegExp(filterValue[0] || "", "i"),
  }));
  return datas.filter((data) => {
    let matched = true;
    for (const { field, matcher } of matchers) {
      if (!matcher.test(data[field])) {
        matched = false;
        break;
      }
    }
    return matched;
  });
}

const SORT_TYPE = {
  TEXT: "text",
  NUMBER: "number",
};

// normal sort text
function sortor(field) {
  return function (a, b) {
    const aValue = typeof field === "function" ? field(a) : a[field];
    const bValue = typeof field === "function" ? field(b) : b[field];

    const dataA = !isEmpty(aValue) ? aValue : undefined;
    const dataB = !isEmpty(bValue) ? bValue : undefined;

    if (!isEmpty(dataA) && isEmpty(dataB)) {
      return -1;
    } else if (isEmpty(dataA) && !isEmpty(dataB)) {
      return 1;
    } else {
      return String(dataA).localeCompare(
        String(dataB),
        "th",
        DEFAULT_SORT_CONFIG
      );
    }
  };
}

function renderEllipsisText({
  text,
  placement = "topLeft",
  filter,
  field,
  filteredInfo = {},
}) {
  return (
    <Tooltip placement={placement} title={text}>
      <div className="ellipsis">
        {filter
          ? displayTextFilter((filteredInfo[field] || [])[0])(text)
          : text}
      </div>
    </Tooltip>
  );
}

function renderCustomTooltipForEllipsisText({
  text,
  placement = "topLeft",
  filter,
  field,
  filteredInfo = {},
  onView,
}) {
  return (
    <Tooltip
      placement={placement}
      title={
        <div>
          <div className="tooltip-cmd-panel">
            {onView && (
              <span className="tooltip-cmd-btn" onClick={() => onView()}>
                View
              </span>
            )}
            <span
              className="tooltip-cmd-btn"
              onClick={() =>
                copyTextToClipBoard(text, { autoFormatJson: true })
              }
            >
              Copy
            </span>
          </div>
          <div>{text}</div>
        </div>
      }
    >
      <div className="ellipsis">
        {filter
          ? displayTextFilter((filteredInfo[field] || [])[0])(text)
          : text}
      </div>
    </Tooltip>
  );
}

function validateColumnStyle(
  record,
  field,
  overrideClass = {},
  cancelStyleFlag = false
) {
  if (cancelStyleFlag) return "";

  overrideClass = {
    changeClass: "input-change-col-label",
    warnClass: "input-warn-col-label",
    errorClass: "input-error-col-label",
    ...overrideClass,
  };
  return Detector.verify(record, field, overrideClass);
}

// for Hook
const SEARCH_FILTER = (filteredInfo, dataIndex, stuff = {}) => {
  return {
    filterIcon,
    filteredValue: filteredValue(filteredInfo, dataIndex),
    onFilter: onFilterText(stuff.targetSearch || dataIndex),
    filterDropdown: generateFilterPanelHook(stuff, dataIndex),
    onFilterDropdownVisibleChange: onFilterDropdownVisibleChangeHook(stuff, dataIndex),
  };
};

const NEW_SEARCH_HEADER = (filteredInfo, dataIndex, stuff = {}) => {
  return {
    filterIcon,
    filteredValue: filteredValue(filteredInfo, dataIndex),
    onFilter: onFilterText(stuff.targetSearch || dataIndex),
    filterDropdown: newGenerateFilterPanelHook(stuff, dataIndex),
    onFilterDropdownVisibleChange: onFilterDropdownVisibleChangeHook(stuff, dataIndex),
  };
};

const indexed = (paginationInfo) => (_, __, index) => {
  const offset = paginationInfo
    ? (paginationInfo.current - 1) * paginationInfo.pageSize
    : 0;
  return offset + (index + 1);
};

export {
  indexed,
  displayTextFilter,
  displayTextFilter2,
  generateFilterPanel,
  newGenerateFilterPanelHook,
  onFilterText,
  onFilterDropdownVisibleChange,
  filterIcon,
  filteredData,
  filteredValue,
  sortor,
  SORT_TYPE,
  renderEllipsisText,
  renderCustomTooltipForEllipsisText,
  validateColumnStyle,
  SEARCH_FILTER,
  NEW_SEARCH_HEADER,
};
